import { useEffect, useState } from "react";

// Public, unauthenticated, CORS-open mirror of the GitHub contributions
// calendar. Returns 371 days ending today, already carrying a level 0-4.
const ENDPOINT = "https://github-contributions-api.jogruber.de/v4";

const monthFormat = new Intl.DateTimeFormat("en", {
  month: "short",
  timeZone: "UTC",
});

// "Sep '25", matching the reference's row labels.
function monthLabel(date) {
  return `${monthFormat.format(date)} '${date.getUTCFullYear().toString().slice(-2)}`;
}

function normalize(contributions) {
  if (!Array.isArray(contributions) || contributions.length === 0) return [];

  const first = new Date(`${contributions[0].date}T00:00:00Z`);
  const leadingOffset = first.getUTCDay();

  return contributions.map((entry, index) => {
    const date = new Date(`${entry.date}T00:00:00Z`);
    const monthKey = entry.date.slice(0, 7);
    return {
      date: entry.date,
      count: entry.count || 0,
      level: entry.level || 0,
      dayOfMonth: date.getUTCDate(),
      dayOfWeek: date.getUTCDay(),
      weekIndex: Math.floor((index + leadingOffset) / 7),
      monthKey,
      monthLabel: monthLabel(date),
    };
  });
}

/**
 * Fetches a year of real contribution data. Never falls back to synthetic
 * data: if the endpoint is unreachable the caller renders an honest empty
 * state instead of inventing numbers.
 */
export default function useContributions(username) {
  const [state, setState] = useState({
    status: "loading",
    days: [],
    total: 0,
  });

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    setState({ status: "loading", days: [], total: 0 });

    fetch(`${ENDPOINT}/${encodeURIComponent(username)}?y=last`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (cancelled) return;
        const days = normalize(payload.contributions);
        if (days.length === 0) throw new Error("empty calendar");
        setState({
          status: "ready",
          days,
          total: payload.total?.lastYear ?? 0,
        });
      })
      .catch((error) => {
        if (cancelled || error.name === "AbortError") return;
        setState({ status: "error", days: [], total: 0 });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [username]);

  return state;
}
